import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

type PasswordLoginState = {
	passwordLogin: {
		email: string;
		password: string;
		isFormLoading: boolean;
		error: string | null;
		isMfaLoginRequired: boolean;
		isMfaAppSetupRequired: boolean;
		isMfaSmsSetupRequired: boolean;
		isPhoneSetupRequired: boolean;
		formState: {
			authCode: string;
		};
	};
};

type PasswordLoginActions = {
	setEmail: (email: PasswordLoginState['passwordLogin']['email']) => void;
	setPassword: (password: PasswordLoginState['passwordLogin']['password']) => void;
	setIsFormLoading: (isLoading: PasswordLoginState['passwordLogin']['isFormLoading']) => void;
	setError: (error: PasswordLoginState['passwordLogin']['error']) => void;
	setIsMfaLoginRequired: (isMfaLoginRequired: PasswordLoginState['passwordLogin']['isMfaLoginRequired']) => void;
	setIsMfaAppSetupRequired: (
		isMfaAppSetupRequired: PasswordLoginState['passwordLogin']['isMfaAppSetupRequired'],
	) => void;
	setIsMfaSmsSetupRequired: (
		isMfaSmsSetupRequired: PasswordLoginState['passwordLogin']['isMfaSmsSetupRequired'],
	) => void;
	setIsPhoneSetupRequired: (isPhoneSetupRequired: PasswordLoginState['passwordLogin']['isPhoneSetupRequired']) => void;
	setFormState: (formState: PasswordLoginState['passwordLogin']['formState']) => void;
	resetPasswordLoginState: () => void;
};

export type PasswordLoginSlice = PasswordLoginState & PasswordLoginActions;

const passwordLoginInitialState: PasswordLoginState = {
	passwordLogin: {
		email: '',
		password: '',
		isFormLoading: false,
		error: null,
		isMfaLoginRequired: false,
		isMfaAppSetupRequired: false,
		isMfaSmsSetupRequired: false,
		isPhoneSetupRequired: false,
		formState: {
			authCode: '',
		},
	},
};

export function passwordLoginSlice(set: NamedSet<ZustandStore>): PasswordLoginSlice {
	return {
		...passwordLoginInitialState,
		setEmail: (email: PasswordLoginState['passwordLogin']['email']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.email = email;
					return state;
				},
				false,
				'[setEmail]',
			);
		},
		setPassword: (password: PasswordLoginState['passwordLogin']['password']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.password = password;
					return state;
				},
				false,
				'[setPassword]',
			);
		},
		setIsFormLoading: (isLoading: PasswordLoginState['passwordLogin']['isFormLoading']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isFormLoading = isLoading;
					return state;
				},
				false,
				'[setIsFormLoading]',
			);
		},
		setError: (error: PasswordLoginState['passwordLogin']['error']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.error = error;
					return state;
				},
				false,
				'[setError]',
			);
		},
		setIsMfaLoginRequired: (isMfaLoginRequired: PasswordLoginState['passwordLogin']['isMfaLoginRequired']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isMfaLoginRequired = isMfaLoginRequired;
					return state;
				},
				false,
				'[setIsMfaLoginRequired]',
			);
		},
		setIsMfaAppSetupRequired(isMfaAppSetupRequired: PasswordLoginState['passwordLogin']['isMfaAppSetupRequired']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isMfaAppSetupRequired = isMfaAppSetupRequired;
					return state;
				},
				false,
				'[setIsMfaAppSetupRequired]',
			);
		},
		setIsMfaSmsSetupRequired(isMfaSmsSetupRequired: PasswordLoginState['passwordLogin']['isMfaSmsSetupRequired']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isMfaSmsSetupRequired = isMfaSmsSetupRequired;
					return state;
				},
				false,
				'[setIsMfaSmsSetupRequired]',
			);
		},
		setIsPhoneSetupRequired(isPhoneSetupRequired: PasswordLoginState['passwordLogin']['isPhoneSetupRequired']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isPhoneSetupRequired = isPhoneSetupRequired;
					return state;
				},
				false,
				'[setIsPhoneSetupRequired]',
			);
		},
		setFormState(formState: PasswordLoginState['passwordLogin']['formState']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.formState = formState;
					return state;
				},
				false,
				'[setFormState]',
			);
		},
		resetPasswordLoginState() {
			set(
				(state: PasswordLoginState) => {
					state = passwordLoginInitialState;
					return state;
				},
				false,
				'[resetPasswordLoginState]',
			);
		},
	};
}
